import React, { useRef } from 'react';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform
} from 'framer-motion';

/* Spring-physics 3D tilt (adapted from 21st.dev "Tilt" by ibelick) with an
 * added cursor-following sheen. Mouse-only by nature — touch devices simply
 * see the card flat, which is the right fallback. */

const DEFAULT_SPRING = { stiffness: 120, damping: 14, mass: 0.3 };

const Tilt3D = ({
  children,
  className,
  rotationFactor = 6,
  sheen = true,
  style
}) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const xSpring = useSpring(x, DEFAULT_SPRING);
  const ySpring = useSpring(y, DEFAULT_SPRING);

  const rotateX = useTransform(ySpring, [-0.5, 0.5], [rotationFactor, -rotationFactor]);
  const rotateY = useTransform(xSpring, [-0.5, 0.5], [-rotationFactor, rotationFactor]);
  const transform = useMotionTemplate`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;

  const sheenX = useTransform(xSpring, [-0.5, 0.5], [15, 85]);
  const sheenY = useTransform(ySpring, [-0.5, 0.5], [15, 85]);
  const sheenOpacity = useSpring(0, { stiffness: 200, damping: 25 });
  const sheenBg = useMotionTemplate`radial-gradient(320px circle at ${sheenX}% ${sheenY}%, rgba(255, 248, 240, 0.35), transparent 65%)`;

  const onMouseMove = (e) => {
    if (reduce || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
    sheenOpacity.set(1);
  };

  const onMouseLeave = () => {
    x.set(0);
    y.set(0);
    sheenOpacity.set(0);
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={reduce ? style : { transformStyle: 'preserve-3d', ...style, transform }}
      onMouseMove={reduce ? undefined : onMouseMove}
      onMouseLeave={reduce ? undefined : onMouseLeave}
    >
      {children}
      {sheen && !reduce && (
        <motion.span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            pointerEvents: 'none',
            zIndex: 3,
            background: sheenBg,
            opacity: sheenOpacity
          }}
        />
      )}
    </motion.div>
  );
};

export default Tilt3D;
